import { useMemo } from "react";

import type { ClientRow, ManagedSite } from "../data/clients-sites";

export type ContactTargetValue = `client:${string}` | `site:${string}` | "";

type TargetPillTone = "client" | "site" | "live" | "setup" | "count";

function siteStatusLabel(status: string): { label: string; tone: "live" | "setup" } {
  return status === "live" ? { label: "Live", tone: "live" } : { label: "Setup", tone: "setup" };
}

function TargetOption({
  optionValue,
  selected,
  title,
  meta,
  pills,
  onSelect,
}: {
  optionValue: string;
  selected: boolean;
  title: string;
  meta: string | null;
  pills: readonly { label: string; tone: TargetPillTone }[];
  onSelect: (value: string) => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`target-picker__option${selected ? " target-picker__option--selected" : ""}`}
      onClick={() => onSelect(optionValue)}
    >
      <span className="target-picker__option-main">
        <span className="target-picker__option-title">{title}</span>
        {meta !== null && meta.length > 0 ? (
          <span className="target-picker__option-meta">{meta}</span>
        ) : null}
      </span>
      <span className="target-picker__pills">
        {pills.map((pill) => (
          <span
            key={`${optionValue}-${pill.label}`}
            className={`target-picker__pill target-picker__pill--${pill.tone}`}
          >
            {pill.label}
          </span>
        ))}
      </span>
    </button>
  );
}

/** Grouped client/site picker for contact assignments (drawer-friendly hierarchy + pills). */
export function ContactTargetPicker({
  clients,
  sites,
  clientNames,
  value,
  onChange,
  required = false,
}: {
  clients: ClientRow[];
  sites: ManagedSite[];
  clientNames: ReadonlyMap<string, string>;
  value: ContactTargetValue;
  onChange: (next: ContactTargetValue) => void;
  required?: boolean;
}) {
  const sortedClients = useMemo(
    () => [...clients].sort((left, right) => left.name.localeCompare(right.name)),
    [clients],
  );

  const sitesByClient = useMemo(() => {
    const grouped = new Map<string, ManagedSite[]>();
    for (const site of sites) {
      const bucket = grouped.get(site.clientId) ?? [];
      bucket.push(site);
      grouped.set(site.clientId, bucket);
    }
    for (const bucket of grouped.values()) {
      bucket.sort((left, right) => left.name.localeCompare(right.name));
    }
    return grouped;
  }, [sites]);

  const clientIdsWithSites = useMemo(() => {
    return [...sitesByClient.keys()].sort((left, right) =>
      (clientNames.get(left) ?? "").localeCompare(clientNames.get(right) ?? ""),
    );
  }, [clientNames, sitesByClient]);

  return (
    <fieldset className="target-picker tpl__field">
      <legend className="target-picker__legend">Target client or site</legend>
      <div className="target-picker__groups" role="radiogroup" aria-required={required || undefined}>
        {sortedClients.length > 0 ? (
          <section className="target-picker__group" aria-label="Client-wide coverage" role="region">
            <header className="target-picker__group-head">
              <span className="target-picker__group-title">Client-wide coverage</span>
              <span className="target-picker__pill target-picker__pill--count">
                {sortedClients.length}
              </span>
            </header>
            <div className="target-picker__options">
              {sortedClients.map((client) => (
                <TargetOption
                  key={`client:${client.clientId}`}
                  optionValue={`client:${client.clientId}`}
                  selected={value === `client:${client.clientId}`}
                  title={client.name}
                  meta="All sites under this client"
                  pills={[{ label: "Client", tone: "client" }]}
                  onSelect={(next) => onChange(next as ContactTargetValue)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {clientIdsWithSites.map((clientId) => {
          const clientName = clientNames.get(clientId) ?? "Client";
          const clientSites = sitesByClient.get(clientId) ?? [];
          return (
            <section
              key={clientId}
              className="target-picker__group"
              aria-label={`Sites for ${clientName}`}
              role="region"
            >
              <header className="target-picker__group-head">
                <span className="target-picker__group-title">{clientName}</span>
                <span className="target-picker__pill target-picker__pill--count">
                  {clientSites.length} site{clientSites.length === 1 ? "" : "s"}
                </span>
              </header>
              <div className="target-picker__options">
                {clientSites.map((site) => {
                  const status = siteStatusLabel(site.status);
                  const locality =
                    site.locality !== null && site.locality.length > 0
                      ? `${site.locality}${site.region !== null && site.region.length > 0 ? `, ${site.region}` : ""}`
                      : null;
                  return (
                    <TargetOption
                      key={`site:${site.siteId}`}
                      optionValue={`site:${site.siteId}`}
                      selected={value === `site:${site.siteId}`}
                      title={site.name}
                      meta={locality}
                      pills={[
                        { label: "Site", tone: "site" },
                        { label: status.label, tone: status.tone },
                      ]}
                      onSelect={(next) => onChange(next as ContactTargetValue)}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      {required && value === "" ? (
        <p className="target-picker__hint">Choose where this contact applies.</p>
      ) : null}
    </fieldset>
  );
}
